import { useEffect, useState } from 'react';
import { Canvas } from './components/Canvas';
import { Inspector } from './components/Inspector';
import { Palette } from './components/Palette';
import { useStore, activeSheet } from './store';
import { ToastHost } from './components/Toast';
import { ErrorBoundary } from './components/ErrorBoundary';

/**
 * Stripped-down second-window app. Locked to a single sheet so the user can
 * watch it on a second monitor while editing the main project elsewhere.
 *
 * Layout: just the toolbar strip (logo + sheet name) + canvas + inspector.
 * No project tree, no sheet management — this is a "viewport into one
 * sheet" rather than a separate project window.
 *
 * State is synced bi-directionally with the main window via BroadcastChannel
 * (see windowSync.ts). Edits made here propagate instantly back; edits in
 * the main window appear here within ~50 ms.
 */
export default function PopoutApp({ sheetId }: { sheetId: string }) {
  const setActiveSheet = useStore((s) => s.setActiveSheet);
  const sheet = useStore(activeSheet);
  const projectName = useStore((s) => s.meta.name);
  const theme = useStore((s) => s.theme);
  const [waitingForSync, setWaitingForSync] = useState(true);

  // Lock active sheet to the popout id whenever sheets change.
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
  }, [theme]);

  useEffect(() => {
    setActiveSheet(sheetId);
  }, [sheetId, setActiveSheet]);

  // Re-pin every time the store sheets change (the main window may have
  // sent us a fresh snapshot that re-orders or replaces the sheets array).
  const sheets = useStore((s) => s.sheets);
  useEffect(() => {
    const target = sheets.find((s) => s.id === sheetId);
    if (target && useStore.getState().activeSheetId !== sheetId) {
      setActiveSheet(sheetId);
    }
    if (target) setWaitingForSync(false);
  }, [sheets, sheetId, setActiveSheet]);

  return (
    <ErrorBoundary>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          width: '100vw',
          background: 'var(--bg)',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '0 12px',
            height: 32,
            background: 'var(--panel)',
            borderBottom: '1px solid var(--panel-border)',
            fontSize: 12,
            color: 'var(--text)',
          }}
        >
          <span
            style={{
              fontWeight: 700,
              fontSize: 11,
              letterSpacing: 0.5,
              textTransform: 'uppercase',
              color: 'var(--text-muted)',
            }}
          >
            Popout
          </span>
          <span style={{ color: 'var(--text-muted)' }}>/</span>
          <span style={{ fontWeight: 600 }}>{projectName || '(untitled project)'}</span>
          <span style={{ color: 'var(--text-muted)' }}>›</span>
          <span style={{ fontWeight: 600, color: 'var(--accent)' }}>
            {sheet?.name ?? '(loading…)'}
          </span>
          <span style={{ flex: 1 }} />
          <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>
            Synced with main window in real time
          </span>
        </div>

        {waitingForSync ? (
          <div
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--text-muted)',
              fontSize: 13,
            }}
          >
            Waiting for project state from main window…
          </div>
        ) : (
          <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
            {sheet?.kind !== 'interconnect' && <aside
              style={{
                width: 268,
                background: 'var(--panel)',
                borderRight: '1px solid var(--panel-border)',
                flexShrink: 0,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
              }}
            >
              <Palette />
            </aside>}
            <Canvas />
            {sheet?.kind !== 'interconnect' && <Inspector />}
          </div>
        )}
        <ToastHost />
      </div>
    </ErrorBoundary>
  );
}
